import type { UseCase } from '../data';

export function UseCaseVisual({ u }: { u: UseCase }) {
  if (u.chart === 'compare' && u.before != null && u.after != null) {
    const max = Math.max(u.before, u.after);
    const pair = [
      { label: 'Before',         val: u.before, isAfter: false },
      { label: 'With CallXcel',  val: u.after,  isAfter: true  },
    ];
    return (
      <div className="uc-viz uc-viz-compare">
        {pair.map((p) => (
          <div className="uc-cmp" key={p.label}>
            <div className="uc-cmp-bar-wrap">
              <div className="uc-cmp-bar" style={{
                height: `${(p.val / max) * 100}%`,
                background: p.isAfter ? u.tintFg : 'var(--border-strong)',
              }}>
                <span className="uc-cmp-val" style={{ color: p.isAfter ? u.tintFg : 'var(--text-3)' }}>
                  {p.val}{u.unit ?? ''}
                </span>
              </div>
            </div>
            <span className="uc-cmp-label">{p.label}</span>
          </div>
        ))}
      </div>
    );
  }

  if (u.chart === 'donut' && u.value != null) {
    const r = 54, c = 2 * Math.PI * r;
    const off = c - (u.value / 100) * c;
    return (
      <div className="uc-viz uc-viz-donut">
        <svg viewBox="0 0 140 140" width={160} height={160}>
          <circle cx={70} cy={70} r={r} fill="none" stroke="var(--bg-tint)" strokeWidth={14} />
          <circle
            cx={70} cy={70} r={r} fill="none" stroke={u.tintFg} strokeWidth={14}
            strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off}
            transform="rotate(-90 70 70)"
            className="uc-anim-donut"
          />
          <text x={70} y={68} textAnchor="middle" fontSize={30} fontWeight={800} fill="var(--text)">
            {u.value}%
          </text>
          <text x={70} y={88} textAnchor="middle" fontSize={11} fill="var(--text-3)">
            {u.metricLabel}
          </text>
        </svg>
      </div>
    );
  }

  if (u.chart === 'line' && u.series) {
    const w = 360, h = 190, padX = 14, padTop = 16, padBottom = 28;
    const data = u.series;
    const labels = u.labels ?? data.map((_, i) => `M${i + 1}`);
    const max = Math.max(...data);
    const pts = data.map((d, i): [number, number] => [
      padX + (i / (data.length - 1)) * (w - padX * 2),
      (h - padBottom) - (d / max) * (h - padTop - padBottom),
    ]);
    const line = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
    const area = `${line} L${pts[pts.length - 1][0].toFixed(1)},${h - padBottom} L${pts[0][0].toFixed(1)},${h - padBottom} Z`;
    const fillId = `ucFill-${u.icon}`;
    return (
      <div className="uc-viz uc-viz-line">
        <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="xMidYMid meet">
          <defs>
            <linearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor={u.tintFg} stopOpacity="0.28" />
              <stop offset="100%" stopColor={u.tintFg} stopOpacity="0" />
            </linearGradient>
          </defs>
          {[0, 0.5, 1].map((g) => {
            const y = padTop + g * (h - padTop - padBottom);
            return <line key={g} x1={padX} x2={w - padX} y1={y} y2={y} stroke="var(--border)" strokeWidth={1} strokeDasharray="3 5" />;
          })}
          <path d={area} fill={`url(#${fillId})`} className="uc-anim-line" />
          <path d={line} fill="none" stroke={u.tintFg} strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" className="uc-anim-line" />
          {pts.map((p, i) => (
            <circle key={`c${i}`} cx={p[0]} cy={p[1]} r={3.5} fill="#fff" stroke={u.tintFg} strokeWidth={2} />
          ))}
          {pts.map((p, i) => (
            <text key={`l${i}`} x={p[0]} y={h - 8} textAnchor="middle" fontSize={11} fill="var(--text-4)">
              {labels[i]}
            </text>
          ))}
        </svg>
      </div>
    );
  }

  // bars
  if (u.chart === 'bars' && u.series && u.labels) {
    const max = Math.max(...u.series);
    return (
      <div className="uc-viz uc-viz-bars">
        <div className="uc-bars-row">
          {u.series.map((v, i) => (
            <div className="uc-bar-col" key={i}>
              <div className="uc-bar" style={{
                height: `${(v / max) * 100}%`,
                background: `linear-gradient(180deg, ${u.tintFg}cc, ${u.tintFg})`,
              }} />
              <span className="uc-bar-x">{u.labels![i]}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return null;
}
